// Client-side mirror of dprEngine.js's computeRepayment, trimmed to what the
// wizard's live preview/schedule needs. Kept here rather than imported from
// ca-backend since CRA's webpack build can't reach outside src/.
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

// "Mon-YY" calendar label for the Nth month (1-indexed) after a disbursement
// date — e.g. disbursementDate=2024-03-15, monthIndex=1 -> "Mar-24".
export function monthLabel(disbursementDate, monthIndex) {
  if (!disbursementDate) return `Month ${monthIndex}`;
  const d = new Date(disbursementDate);
  if (Number.isNaN(d.getTime())) return `Month ${monthIndex}`;
  d.setMonth(d.getMonth() + (monthIndex - 1));
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }).replace(' ', '-');
}

// Converts a tranche's own date into a 1-based month index relative to the
// loan's disbursement date (month 1) — so a CA can enter each disbursement
// tranche on its real calendar date rather than typing a month offset.
export function monthIndexFromDate(disbursementDate, date) {
  const d0 = new Date(disbursementDate), d1 = new Date(date);
  if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) return 1;
  return (d1.getFullYear() - d0.getFullYear()) * 12 + (d1.getMonth() - d0.getMonth()) + 1;
}

// Month-by-month schedule. Two disbursement modes:
//   - 'lump_sum' (default): full sanctioned amount drawn in month 1, same as
//     before.
//   - 'installment_wise': drawn in tranches (date + amount) — interest each
//     month is charged on the balance AFTER that month's disbursement lands
//     (verified against a real bank-issued schedule: interest = cumulative
//     disbursed × rate/12 for that month, not on the undrawn sanctioned
//     amount), so the loan "ramps up" instead of accruing interest on money
//     that hasn't actually been released yet.
// `subsidyPrepayments` (array of {month, amount}, or a single {month,amount}
// object for backward compatibility) is optional — one entry per Capital
// Subsidy disbursement tranche, each landing as a lump-sum principal
// reduction in its own month, same convention as the server-side engine.
export function repaymentMonths(termLoanConfig, subsidyPrepayments) {
  const prepayments = !subsidyPrepayments ? [] : Array.isArray(subsidyPrepayments) ? subsidyPrepayments : [subsidyPrepayments];
  const ratePct = Number(termLoanConfig.ratePct) || 0;
  const tenorMonths = Number(termLoanConfig.tenorMonths) || 60;
  const moratoriumMonths = Number(termLoanConfig.moratoriumMonths) || 0;
  const isInstallmentWise = termLoanConfig.disbursementType === 'installment_wise';

  let disbursementByMonth = {};
  let principal;
  if (isInstallmentWise && (termLoanConfig.disbursementTranches || []).length) {
    (termLoanConfig.disbursementTranches || []).forEach(t => {
      const m = monthIndexFromDate(termLoanConfig.disbursementDate, t.date);
      disbursementByMonth[m] = (disbursementByMonth[m] || 0) + (Number(t.amount) || 0);
    });
    principal = Object.values(disbursementByMonth).reduce((s, v) => s + v, 0);
  } else {
    principal = Number(termLoanConfig.amount) || 0;
    disbursementByMonth = { 1: principal };
  }

  const repaymentSpan = Math.max(tenorMonths - moratoriumMonths, 1);
  const monthlyInstallment = principal / repaymentSpan;

  let balance = 0;
  const months = [];
  for (let m = 1; m <= tenorMonths; m++) {
    const openingBalance = balance;
    const disbursement = round2(disbursementByMonth[m] || 0);
    const balanceForInterest = openingBalance + disbursement;
    const interest = balanceForInterest * (ratePct / 100) / 12;
    const installment = m <= moratoriumMonths ? 0 : Math.min(monthlyInstallment, balanceForInterest);
    const thisMonthPrepayments = prepayments.filter(p => p.month === m);
    const subsidyRepay = thisMonthPrepayments.length
      ? Math.min(thisMonthPrepayments.reduce((s, p) => s + (p.amount || 0), 0), balanceForInterest - installment)
      : 0;
    const closingBalance = Math.max(balanceForInterest - installment - subsidyRepay, 0);
    months.push({ month: m, openingBalance, disbursement, interest, installment, subsidyRepay, closingBalance });
    balance = closingBalance;
  }
  return months;
}

export function previewRepaymentByYear(termLoanConfig, projectionYears, subsidyPrepayment) {
  const months = repaymentMonths(termLoanConfig, subsidyPrepayment);
  const years = Array.from({ length: projectionYears || 6 }, (_, i) => i + 1);
  return years.map(y => {
    const yearMonths = months.filter(m => Math.ceil(m.month / 12) === y);
    return {
      year: y,
      openingBalance: yearMonths.length ? round2(yearMonths[0].openingBalance) : 0,
      disbursement: round2(yearMonths.reduce((s, m) => s + m.disbursement, 0)),
      interest: round2(yearMonths.reduce((s, m) => s + m.interest, 0)),
      installment: round2(yearMonths.reduce((s, m) => s + m.installment, 0)),
      subsidyRepay: round2(yearMonths.reduce((s, m) => s + m.subsidyRepay, 0)),
      closingBalance: yearMonths.length ? round2(yearMonths[yearMonths.length - 1].closingBalance) : 0,
    };
  });
}

// IDCP (Interest During Construction Period) — the interest that accrues on
// the term loan during the moratorium, before any installment is due. Pure
// function of the loan's own fields (correctly reflecting a progressive
// installment-wise drawdown, not the full amount from day one), so it's
// always derivable rather than a number someone has to separately type in
// and keep in sync by hand.
export function computeIDCP(termLoanConfig) {
  const moratoriumMonths = Number(termLoanConfig.moratoriumMonths) || 0;
  if (!moratoriumMonths) return 0;
  const months = repaymentMonths(termLoanConfig);
  return round2(months.filter(m => m.month <= moratoriumMonths).reduce((s, m) => s + m.interest, 0));
}
